import {
  Checkbox,
  type ConfirmModalProps,
  Input,
  type Notification,
  notify,
  toast,
  type ToastOptions,
  toReactNode,
  type useConfirmModal,
} from '@affine/component';
import {
  NotificationExtension,
  type NotificationService,
} from '@blocksuite/affine/shared/services';
import { useCallback, useState } from 'react';

// sl-optc: localStorage prefix for "don't show again" preferences
const DONT_SHOW_AGAIN_PREFIX = 'streamlain:dontShowAgain:';

/**
 * Stateful checkbox wrapper — Checkbox component is fully controlled, needs React state.
 * Uses <div> instead of <label> to prevent native label-input association from causing
 * a double-toggle: <label> wrapping <input> dispatches a synthetic click that bubbles
 * back up and fires onClick twice → net zero toggle.
 */
function DontShowAgainCheckbox({
  onToggle,
}: {
  onToggle: (checked: boolean) => void;
}) {
  const [checked, setChecked] = useState(false);
  const toggle = useCallback(() => {
    setChecked(prev => {
      const next = !prev;
      onToggle(next);
      return next;
    });
  }, [onToggle]);
  return (
    <div
      role="checkbox"
      aria-checked={checked}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        fontSize: 13,
        color: 'var(--streamlain-v2-text-secondary)',
        cursor: 'pointer',
        padding: '4px 0',
      }}
      onClick={e => {
        // If click originated from the native <input> inside Checkbox,
        // the Checkbox onChange already handled it — skip to avoid double-toggle
        if ((e.target as HTMLElement).tagName === 'INPUT') return;
        toggle();
      }}
    >
      <Checkbox
        checked={checked}
        onChange={(_e, newChecked) => {
          setChecked(newChecked);
          onToggle(newChecked);
        }}
      />
      Don&apos;t show again
    </div>
  );
}

export class NotificationServiceImpl implements NotificationService {
  constructor(
    private readonly closeConfirmModal: () => void,
    private readonly openConfirmModal: (props: ConfirmModalProps) => void
  ) {}

  confirm = async ({
    title,
    message,
    confirmText,
    cancelText,
    abort,
    dontShowAgainKey,
  }: Parameters<NotificationService['confirm']>[0]) => {
    // sl-optc: Check if user has opted out of this dialog
    if (dontShowAgainKey) {
      const storageKey = DONT_SHOW_AGAIN_PREFIX + dontShowAgainKey;
      if (localStorage.getItem(storageKey) === 'true') {
        return true; // Auto-confirm without showing dialog
      }
    }

    return new Promise<boolean>(resolve => {
      // sl-optc: Track checkbox state
      let dontShowAgainChecked = false;

      // Build description with optional checkbox
      const descriptionContent = dontShowAgainKey ? (
        <div>
          <div style={{ marginBottom: 12 }}>{toReactNode(message)}</div>
          <DontShowAgainCheckbox
            onToggle={checked => {
              dontShowAgainChecked = checked;
            }}
          />
        </div>
      ) : (
        toReactNode(message)
      );

      this.openConfirmModal({
        title: toReactNode(title),
        description: descriptionContent,
        confirmText,
        confirmButtonOptions: {
          variant: 'primary',
        },
        cancelText,
        onConfirm: () => {
          // sl-optc: Save preference if checkbox was checked
          if (dontShowAgainKey && dontShowAgainChecked) {
            const storageKey = DONT_SHOW_AGAIN_PREFIX + dontShowAgainKey;
            localStorage.setItem(storageKey, 'true');
          }
          resolve(true);
        },
        onCancel: () => {
          resolve(false);
        },
      });
      abort?.addEventListener('abort', () => {
        resolve(false);
        this.closeConfirmModal();
      });
    });
  };

  prompt = async ({
    title,
    message,
    confirmText,
    placeholder,
    cancelText,
    autofill,
    abort,
  }: Parameters<NotificationService['prompt']>[0]) => {
    return new Promise<string | null>(resolve => {
      let value = autofill || '';
      const description = (
        <div>
          <span style={{ marginBottom: 12 }}>{toReactNode(message)}</span>
          <Input
            autoSelect={true}
            placeholder={placeholder}
            defaultValue={value}
            onChange={e => (value = e)}
          />
        </div>
      );
      this.openConfirmModal({
        title: toReactNode(title),
        description: description,
        confirmText: confirmText ?? 'Confirm',
        confirmButtonOptions: {
          variant: 'primary',
        },
        cancelText: cancelText ?? 'Cancel',
        onConfirm: () => {
          resolve(value);
        },
        onCancel: () => {
          resolve(null);
        },
        autoFocusConfirm: false,
      });
      abort?.addEventListener('abort', () => {
        resolve(null);
        this.closeConfirmModal();
      });
    });
  };

  toast = (message: string, options: ToastOptions) => {
    return toast(message, options);
  };

  notify = (notification: Parameters<NotificationService['notify']>[0]) => {
    const accentToNotify = {
      error: notify.error,
      success: notify.success,
      warning: notify.warning,
      info: notify,
    };

    const fn = accentToNotify[notification.accent || 'info'];
    if (!fn) {
      throw new Error('Invalid notification accent');
    }

    const toAffineNotificationActions = (
      actions: (typeof notification)['actions']
    ): Notification['actions'] => {
      if (!actions) return undefined;

      return actions.map(({ label, onClick, key }) => {
        return {
          key,
          label: toReactNode(label),
          onClick,
        };
      });
    };

    const toastId = fn(
      {
        title: toReactNode(notification.title),
        message: toReactNode(notification.message),
        actions: toAffineNotificationActions(notification.actions),
        onDismiss: notification.onClose,
      },
      {
        duration: notification.duration || 0,
        onDismiss: notification.onClose,
        onAutoClose: notification.onClose,
      }
    );

    notification.abort?.addEventListener('abort', () => {
      notify.dismiss(toastId);
    });
  };

  notifyWithUndoAction = (
    options: Parameters<NotificationService['notifyWithUndoAction']>[0]
  ) => {
    this.notify(options);
  };
}

export function patchNotificationService({
  closeConfirmModal,
  openConfirmModal,
}: ReturnType<typeof useConfirmModal>) {
  const notificationService = new NotificationServiceImpl(
    closeConfirmModal,
    openConfirmModal
  );
  return NotificationExtension(notificationService);
}
